import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import RefreshOverlay from 'Components/DesignSystem/Table/RefreshOverlay';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import { ArrowDown, ArrowRight, Sort, Hashtag } from 'Components/DesignSystem/Iconography/Icons';

// Local Components
import { ActionBar, RowActionsMenu, ExpandedRowContent, LoadingOverlay, TablePaginationBar } from '../../components';
import { ConfirmActionModal } from '../../modals';

// API Services
import { getDataReviewProducts, BulkActionType } from 'api/DataReview';

// Utilities
import { transformToIgnoredUpdate, transformPagination, PaginationInfo } from '../../utils';
import { useBulkAction } from '../../hooks';

// Types
import { IgnoredUpdate, RowActionType } from '../../types';

// Constants
import { IGNORED_ACTIONS, IGNORED_BULK_ROW_ACTIONS } from '../../constants';

// View props interface
interface IgnoredUpdatesViewProps {
  channelKey: string;
  onRefresh?: () => void;
  hasUpdatePermission?: boolean;
}

// Internal row state type
interface IgnoredUpdateRowState {
  product: IgnoredUpdate;
  isSelected: boolean;
  isExpanded: boolean;
}

// Default pagination values
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;

// Sort configuration
type SortField = 'updated_at' | 'name' | 'category' | 'total_new_count' | 'total_updates_count';
type SortOrder = 'asc' | 'desc';

const DEFAULT_SORT_BY: SortField = 'updated_at';
const DEFAULT_SORT_ORDER: SortOrder = 'desc';

/**
 * IgnoredUpdatesView Component
 *
 * Displays attribute updates that have been marked as ignored using SINGLE UNIFIED TABLE
 * Columns: Checkbox | # | Product | Category | Moved from | Update | New | Expand
 *
 * Key differences from IgnoredProductsView:
 * - NO Row Action dropdown in ActionBar (just Search + Filter)
 * - NO RowActionsMenu column (no 3-dot menu per row)
 * - Has Update and New badge columns instead of Comment and Actions columns
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11680-16809
 */
const IgnoredUpdatesView: React.FC<IgnoredUpdatesViewProps> = ({ channelKey, onRefresh, hasUpdatePermission = true }) => {
  // State
  const [searchValue, setSearchValue] = useState('');
  // Debounced mirror of searchValue. The data fetch keys off this (not the raw
  // value) so typing fires ONE request after 300ms instead of one per keystroke
  // (search=c, search=co, search=col, ...). The input stays bound to searchValue
  // for instant feedback. Mirrors the debounce already used in MissingMappingView.
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchValue(searchValue.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchValue]);
  const [ignoredRows, setIgnoredRows] = useState<IgnoredUpdateRowState[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
    totalPages: 0,
  });

  // Computed values
  const selectedCount = ignoredRows.filter((r) => r.isSelected).length;

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean; message: string; confirmLabel: string; onConfirm: () => void;
  }>({ isOpen: false, message: '', confirmLabel: 'Move Back', onConfirm: () => {} });
  const showConfirm = (message: string, onConfirm: () => void, confirmLabel = 'Move Back') => {
    setConfirmModal({ isOpen: true, message, confirmLabel, onConfirm });
  };
  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    clearSelection();
  };

  // Clear select-all + per-row selection state.
  const clearSelection = () => {
    setSelectAll(false);
    setIgnoredRows((prev) => prev.map((r) => (r.isSelected ? { ...r, isSelected: false } : r)));
  };

  // Tracks whether the component is still mounted. Guards setState calls
  // inside async fetchProducts so a fetch that resolves after tab-switch
  // doesn't fire a "state update on unmounted component" warning.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Fetch products from API
   */
  const fetchProducts = useCallback(async () => {
    if (!channelKey) return;

    setIsLoading(true);
    try {
      const response = await getDataReviewProducts(channelKey, {
        tab: 'ignored_updates',
        page: pagination.page,
        per_page: pagination.perPage,
        search: debouncedSearchValue || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (!isMountedRef.current) return;

      if (response.done && response.data) {
        // Transform API data to frontend format
        const transformedRows = response.data.map((product) => ({
          product: transformToIgnoredUpdate(product),
          isSelected: false,
          isExpanded: false,
        }));
        setIgnoredRows(transformedRows);
        setPagination(transformPagination(response.page));
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Failed to fetch ignored updates:', error);
      toast.error('Failed to load ignored updates');
      setIgnoredRows([]);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [channelKey, pagination.page, pagination.perPage, debouncedSearchValue, sortBy, sortOrder]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Refresh after an action clears the search and resets pagination so the
  // user sees the full list (the acted-on row moves out of this tab and
  // would be hidden by a stale search filter).
  // fetchProducts() direct call is only fired when state didn't change,
  // to avoid racing with the useEffect-driven refetch (stale-closure bug).
  const refreshAfterAction = () => {
    const searchWasSet = searchValue !== '';
    const pageWasAtDefault = pagination.page === DEFAULT_PAGE;
    setSearchValue('');
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    if (!searchWasSet && pageWasAtDefault) {
      fetchProducts();
    }
    clearSelection();
    onRefresh?.();
  };

  // Hooks
  const { isBulkActionLoading, executeBulkAction } = useBulkAction({ channelKey, sourceTab: 'ignored_updates', onTaskComplete: () => { refreshAfterAction(); } });

  // Handlers
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleBulkAction = async (action: string) => {
    const selectedSkus = ignoredRows.filter((r) => r.isSelected).map((r) => r.product.sku).filter(Boolean) as string[];
    if (selectedSkus.length === 0) {
      toast.error('No products selected');
      return;
    }

    showConfirm(
      `Are you sure you want to move ${selectedSkus.length} selected update(s) back to their original source?`,
      () => {
        closeConfirm();
        executeBulkAction(selectedSkus, action as BulkActionType).then((success) => {
          if (success) { refreshAfterAction(); }
        });
      }
    );
  };

  const handleRowAction = async (index: number, actionType: RowActionType) => {
    if (actionType !== 'restore') return;

    const sku = ignoredRows[index]?.product?.sku;
    if (!sku) {
      toast.error('Unable to determine product SKU');
      return;
    }

    showConfirm(
      'Are you sure you want to move this update back to its original source?',
      () => {
        closeConfirm();
        executeBulkAction([sku], 'restore').then((success) => {
          if (success) { refreshAfterAction(); }
        });
      }
    );
  };

  const handleFilterClick = () => {
    // TODO: Implement filter functionality
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const renderSortIcon = (field: SortField) => {
    if (sortBy === field) {
      return (
        <span className={`tw-text-accent ${sortOrder === 'asc' ? 'tw-rotate-180' : ''} tw-inline-block tw-transition-transform`}>
          <ArrowDown />
        </span>
      );
    }
    return <span className="tw-text-ink-tertiary"><Sort /></span>;
  };

  const renderMetadataField = (label: string, value: string) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">{label}</span>
      <span className="tw-text-xs tw-font-semibold tw-text-ink">{value || '-'}</span>
    </div>
  );

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setIgnoredRows((prev) => prev.map((row) => ({ ...row, isSelected: checked })));
  };

  const handleRowSelect = (index: number, selected: boolean) => {
    setIgnoredRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isSelected: selected };
      setSelectAll(updated.every((r) => r.isSelected));
      return updated;
    });
  };

  const handleRowExpand = (index: number) => {
    setIgnoredRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isExpanded: !updated[index].isExpanded };
      return updated;
    });
  };

  const handlePageChange = (newPage: number) => {
    clearSelection();
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPerPage: string | number) => {
    clearSelection();
    setPagination((prev) => ({ ...prev, perPage: newPerPage as number, page: DEFAULT_PAGE }));
  };

  return (
    <div className="tw-space-y-4 tw-relative">
      {/* Action Bar */}
      <ActionBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        /* § 288 — the table fetch, plus the 300ms debounce in front of it. `.trim()`
           on the left because that is what the debounce commits: comparing the raw
           value would leave the spinner running forever on a trailing space. */
        isSearching={isLoading || searchValue.trim() !== debouncedSearchValue}
        bulkActions={IGNORED_BULK_ROW_ACTIONS}
        onBulkAction={handleBulkAction}
        onFilterClick={handleFilterClick}
        isEditMode={false}
        onEditToggle={() => {}}
        selectedCount={selectedCount}
        showEditButton={false}
        isLoading={isBulkActionLoading}
        hasUpdatePermission={hasUpdatePermission}
      />

      {/* Single Unified Table */}
      {(
        <div className="tw-relative tw-border tw-border-edge tw-rounded tw-flex tw-flex-col" style={{ maxHeight: 'calc(100vh - 280px)', minHeight: '400px' }}>
          {/*
            The "Refreshing" chip Guide/ISR shows on the same gesture. These
            tables are hand-rolled rather than the DS `Table`, so they got the
            dim without the chip — the rows faded and nothing said why. Shares
            `Table`'s implementation rather than restating it.

            `rows.length > 0` is the whole condition: a COLD load has nothing to
            dim and shows its spinner row instead, and drawing both would put
            "Refreshing" on top of "Loading...".
          */}
          <RefreshOverlay
            active={isLoading && ignoredRows.length > 0}
            data-testid="catalog-table-refreshing-ignored-updates"
          />
          {/* Header — does not scroll. flex-shrink-0 pins it at natural height;
              overflow:hidden creates a scroll container so
              scrollbar-gutter:stable actually reserves right-side space,
              keeping column edges aligned with the body table below. */}
          <div className="tw-flex-shrink-0" style={{ overflow: 'hidden', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            {/* Column Groups: Checkbox | # | Product | Category | Moved from | Update | New | Actions | Expand */}
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '27%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
            </colgroup>

            {/* Table Header - Two Rows */}
            <thead className="tw-bg-surface">
              {/* Section Header Row */}
              <tr className="tw-bg-surface">
                <th
                  colSpan={9}
                  className="tw-text-center tw-px-4 tw-py-3 tw-border-b tw-border-edge tw-bg-surface"
                >
                  <Typography variant="sub-title-label" className="tw-text-ink">
                    Imported Products
                  </Typography>
                </th>
              </tr>

              {/* Column Headers Row */}
              <tr className="tw-border-b tw-border-edge tw-bg-surface">
                {/* Checkbox column */}
                <th className="tw-px-2 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint">
                  <Checkbox
                    label=""
                    checked={selectAll}
                    indeterminate={selectedCount > 0 && selectedCount < ignoredRows.length}
                    onChange={handleSelectAll}
                  />
                </th>
                {/* # column */}
                <th className="tw-px-2 tw-py-2.5 tw-h-[44px] tw-bg-tone-neutral-tint">
                  <div className="tw-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                    <Hashtag />
                  </div>
                </th>
                {/* Product column (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('name')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Product
                    </Typography>
                    {renderSortIcon('name')}
                  </span>
                </th>
                {/* Category column (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('category')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Category
                    </Typography>
                    {renderSortIcon('category')}
                  </span>
                </th>
                {/* Moved from column */}
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Moved from
                    </Typography>
                  </span>
                </th>
                {/* Update column (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('total_updates_count')}
                >
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Update
                    </Typography>
                    {renderSortIcon('total_updates_count')}
                  </span>
                </th>
                {/* New column (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('total_new_count')}
                >
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      New
                    </Typography>
                    {renderSortIcon('total_new_count')}
                  </span>
                </th>
                {/* Actions column - no header */}
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                  {/* Empty - actions column */}
                </th>
                {/* Expand column - no header icon */}
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                </th>
              </tr>
            </thead>
          </table>
          </div>

          {/* Body — independent scroll container with the SAME colgroup as the
              header. scrollbar-gutter:stable on both sides ensures columns
              stay aligned regardless of whether the vertical scrollbar
              is visible. */}
          <div className="tw-flex-1" style={{ overflow: 'auto', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '27%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
            </colgroup>

            {/* Table Body */}
            <tbody
              /*
                Rows stay mounted through a refetch and DIM, instead of being swapped
                for a spinner row (§ Guide/ISR parity, 2026-08-31). Swapping emptied
                the table body on every page click, so the page height collapsed and
                snapped back and the row under the pointer moved — the change read as
                a reload rather than a page turn.

                `pointer-events-none` matters as much as the opacity: without it the
                stale rows stay clickable, and expanding row 3 of the OLD page while
                the new one lands is a real way to act on data that is no longer there.

                The spinner still shows on a COLD load, where there is nothing to dim.
              */
              aria-busy={isLoading || undefined}
              className={`tw-transition-opacity ${isLoading && ignoredRows.length > 0 ? 'tw-opacity-40 tw-pointer-events-none' : ''}`}
            >
              {/* Loading State */}
              {/* Cold load only — a refetch dims the rows above instead. */}
              {isLoading && ignoredRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="tw-px-4 tw-py-12 tw-text-center">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-[3px]">
                      <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-edge tw-border-t-accent" />
                      <Typography variant="body-primary" className="tw-text-ink-secondary">Loading...</Typography>
                    </div>
                  </td>
                </tr>
              )}
              {ignoredRows.map((row, index) => {
                const isEvenRow = index % 2 === 1;
                const cellBgClass = row.isSelected
                  ? ''
                  : `${isEvenRow ? 'tw-bg-surface-sunken' : 'tw-bg-surface'} group-hover:tw-bg-tone-neutral-tint`;

                return (
                  <React.Fragment key={row.product.id}>
                    {/* Main Row */}
                    <tr
                      className={`tw-group tw-border-b tw-border-edge tw-h-[40px] ${row.isSelected ? 'tw-bg-accent-tint' : 'hover:tw-bg-tone-neutral-tint'}`}
                    >
                      {/* Checkbox */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <Checkbox
                          label=""
                          checked={row.isSelected || false}
                          onChange={(checked) => handleRowSelect(index, checked)}
                        />
                      </td>
                      {/* Row number */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <Typography variant="title-header" className="tw-text-ink">
                            {(pagination.page - 1) * pagination.perPage + index + 1}
                          </Typography>
                        </div>
                      </td>
                      {/* Product name */}
                      <td className={`tw-px-3 tw-py-2 ${cellBgClass}`} title={row.product.name}>
                        <Typography variant="title-header" className="tw-text-ink tw-truncate">
                          {row.product.name}
                        </Typography>
                      </td>
                      {/* Category — wrap in truncating div; `tw-truncate` on
                          a Typography span alone never ellipsizes because it
                          needs a block container + overflow-hidden parent. */}
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`}>
                        <div className="tw-truncate" title={row.product.category}>
                          <Typography variant="title-header" className="tw-text-ink tw-truncate">
                            {(row.product.category || '')}
                          </Typography>
                        </div>
                      </td>
                      {/* Moved from */}
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`}>
                        <div className="tw-truncate" title={(row.product.movedFrom || '')}>
                          <Typography variant="title-header" className="tw-text-ink tw-truncate">
                            {(row.product.movedFrom || '')}
                          </Typography>
                        </div>
                      </td>
                      {/* Update badge - Yellow (Figma: yellow-warning-100 bg, yellow-warning-500 border) */}
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-h-[28px] tw-px-2 tw-py-0.5 tw-rounded-[3px] tw-bg-tone-warning-tint tw-border tw-border-tone-warning-glyph">
                          <Typography variant="title-header" className="tw-text-ink">
                            {row.product.totalUpdatesCount}
                          </Typography>
                        </span>
                      </td>
                      {/* New badge - Grey (Figma: grey-100 bg, grey-200 border, grey-700 text) */}
                      <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-h-[28px] tw-px-2 tw-py-0.5 tw-rounded-[3px] tw-bg-surface-sunken tw-border tw-border-edge">
                          <Typography variant="title-header" className="tw-text-ink-secondary">
                            {row.product.totalNewCount}
                          </Typography>
                        </span>
                      </td>
                      {/* Actions */}
                      <td className={`tw-px-2 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <RowActionsMenu
                          actions={IGNORED_ACTIONS}
                          onAction={(actionType) => handleRowAction(index, actionType)}
                          disabled={!hasUpdatePermission}
                        />
                      </td>
                      {/* Expand */}
                      <td className={`tw-px-2 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <button data-testid={`ignoredupdatesview-ignoredupdatesview-button-1-${index}`}
                          onClick={() => handleRowExpand(index)}
                          className="tw-p-1 tw-rounded hover:tw-bg-surface-hover-strong tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
                        >
                          {row.isExpanded ? <ArrowDown /> : <ArrowRight />}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Row Content */}
                    {row.isExpanded && (
                      <>
                        <ExpandedRowContent
                          // Match the outer colgroup so the # column in the
                          // expanded attribute rows aligns with the outer #.
                          colWidths={['3%', '3%', '27%', '21%', '16%', '10%', '10%', '5%', '5%']}
                          importedAttributes={row.product.attributes || []}
                          catalogAttributes={[]}
                          isEditMode={false}
                          singleSection={true}
                          productName={row.product.name}
                        />
                        <tr className="tw-bg-surface tw-border-t tw-border-edge">
                          <td colSpan={9} className="tw-px-3 tw-py-2">
                            <div className="tw-mb-2">
                              {renderMetadataField('Description', row.product.description || '')}
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-8 tw-mb-2">
                              {renderMetadataField('SKU', row.product.sku || '')}
                              {renderMetadataField('Price', row.product.price || '')}
                              {renderMetadataField('UPC', row.product.upc || '')}
                              {renderMetadataField('Model Number', row.product.modelNumber || '')}
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-8">
                              {renderMetadataField('PLS', row.product.pls || '')}
                              {renderMetadataField('Parent ID', row.product.parentId || '')}
                              {renderMetadataField('Version', row.product.version || '')}
                              {renderMetadataField('Real SKU', row.product.realSku || '')}
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Empty State */}
          {!isLoading && ignoredRows.length === 0 && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                No ignored updates found
              </Typography>
            </div>
          )}
          </div>

          {/*
             The guard no longer includes `!isLoading`. It used to, and the strip therefore
             UNMOUNTED on every page click: the button vanished from under the pointer,
             keyboard focus fell to <body>, and the row it occupied collapsed so the page
             jumped. It stays mounted and takes `disabled` instead — which is what Guide/ISR's
             equivalent does.
          
             What remains here is `total > perPage`, and it stays at the CALL SITE: whether a
             table needs a pager at all is the view's question, not the bar's.
          */}
          {pagination.total > pagination.perPage && (
            <TablePaginationBar
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              disabled={isLoading}
              data-testid="catalog-pagination-ignored-updates"
            />
          )}
        </div>
      )}

      {isBulkActionLoading && <LoadingOverlay />}

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
      />
    </div>
  );
};

export default IgnoredUpdatesView;
